import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import StepHeader from "../StepHeader/StepHeader";
import ActionBar from "../ActionBar/ActionBar";
import { NotificationContainer, NotificationManager } from "react-notifications";
import { APP_STEPS } from "../config/steps";
import "./AcademicUploads.css";
import WhatsAppWidget from "../WhatsAppWidget/WhatsAppWidget";

const Acedemicaploads = () => {
  // ---- state ----
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(Date.now());
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [marksheet, setMarkSheetId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const token = localStorage.getItem("token");
  const applicationId = parseInt(localStorage.getItem("id"), 10);
  const status = localStorage.getItem("application_status");
  const isDisabled = status !== "incomplete";
  const navigate = useNavigate();

  const currentStep = 2; // index in APP_STEPS

  // ---- fetch existing marksheet ----
  useEffect(() => {
    const fetchData = async () => {
      try {
        const campaignRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/campaigns?sort=createdAt:desc&pagination[limit]=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const campaignResponse = await campaignRes.json();
        setLoading(false);

        const latestCampaign = campaignResponse.data[0];
        if (!latestCampaign) return;

        const marksheetRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/academic-uploads?&filters[application][id][$eq]=${applicationId}&populate=marksheet`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const marksheet = await marksheetRes.json();
        const marksheets = marksheet.data;

        if (marksheets.length > 0 && marksheets[0].marksheet) {
          setFormData({
            path: marksheets[0].marksheet.url,
            name: marksheets[0].marksheet.name,
          });
          setMarkSheetId(marksheets[0].id);
        }
      } catch (err) {
        console.error("Error fetching marksheet:", err);
      }
    };
    fetchData();
  }, []);

  // ---- file change ----
  // Accepted marksheet types — kept in sync with the Academic Info step
  const MARKSHEET_ACCEPT = ".pdf,.png,.jpeg,.jpg";
  const MARKSHEET_MIME = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
  const MARKSHEET_MAX_SIZE = 5000000; // 5 MB

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      // Some browsers report an empty type for images — fall back to the extension
      const isAllowedType =
        MARKSHEET_MIME.includes(selectedFile.type) ||
        /\.(pdf|png|jpe?g)$/i.test(selectedFile.name);

      if (selectedFile.size <= MARKSHEET_MAX_SIZE && isAllowedType) {
        setFile(selectedFile);
      } else {
        NotificationManager.error("File must be a JPG, PNG or PDF and less than 5MB");
        setInputKey(Date.now());
      }
    }
  };

  // ---- remove file ----
  const handleRemove = () => {
    setFile(null);
    setInputKey(Date.now());
  };

  // ---- submit (existing API logic kept) ----
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (isDisabled) return;
    if (status === "new") { navigate("/application/finance-details"); return; }
    setErrorMessage("");

    // If no new file but existing file is present, skip upload
    if (!file && formData?.path) {
      navigate("/application/finance-details");
      return;
    }

    if (!file) {
      setErrorMessage("Please upload Mark Sheet before submitting.");
      return;
    }

    setSaving(true);
    try {
      const newFormData = new FormData();
      let response;

      if (marksheet) {
        newFormData.append("application_id", applicationId);
        newFormData.append("marksheet", file);
        response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/academic-uploads/${marksheet}`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
            body: newFormData,
          }
        );
      } else {
        newFormData.append("file", file);
        newFormData.append("application_id", applicationId);
        response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/academic-uploads`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: newFormData,
          }
        );
      }

      if (!response.ok) throw new Error("Failed to upload marksheet");

      const fileData = await response.json();
      const fileInfo = fileData[0] || fileData;
      localStorage.setItem("marksheet", fileInfo.id);

      setInputKey(Date.now());
      setFormData({ path: fileInfo.url, name: fileInfo.name });
      setMarkSheetId(fileInfo.id);

      setSaving(false);
      navigate("/application/finance-details");
    } catch (error) {
      console.error("Error uploading marksheet:", error);
      setSaving(false);
      NotificationManager.error("Error uploading marksheet. Please try again.");
    }
  };

  // SVG upload icon
  const UP_ICON = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 16V4M7 9l5-5 5-5" />
      <path d="M4 16v4h16v-4" />
    </svg>
  );

  return (
    <div>
      {/* Top bar */}
      <DashboardHeader />

      {/* Step progress */}
      <div style={{ background: "rgba(255,255,255,.97)", borderBottom: "1px solid #dbe3e1" }}>
        <div className="au" style={{ paddingBottom: 0 }}>
          <StepHeader steps={APP_STEPS} current={currentStep} />
        </div>
      </div>

      {/* Content */}
      <div className="au">
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <div className="spinner-bubble spinner-bubble-primary"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* page heading */}
            <h2 className="au__title">Academic Uploads</h2>
            <p className="au__sub">Upload your latest marksheet (JPG, PNG or PDF, max 5 MB).</p>
            <p className="au__req-note">Fields marked <em style={{ color: "#a9443b" }}>*</em> are required.</p>

            <div className="au__card">
              <h3>Latest Marksheet</h3>

              {/* Tip note */}
              <div className="au__note">
                📷 Tip: on your phone, tap the upload area and choose <b>Camera</b> to take a photo, or select a PDF.
              </div>

              {/* File upload area */}
              <div className={`au__file${file || formData?.path ? " has" : ""}${errorMessage ? " invalid" : ""}`}>
                <input
                  type="file"
                  id="marksheetInput"
                  accept={MARKSHEET_ACCEPT}
                  onChange={handleFileChange}
                  key={inputKey}
                  hidden
                  disabled={isDisabled}
                />

                {/* Show file selected state */}
                {(file || formData?.path) ? (
                  <div className="au__got">
                    <span className="au__ok">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                        <path d="M5 12l5 5 9-10" />
                      </svg>
                    </span>
                    <span className="au__meta">
                      <span className="au__fname">{file ? file.name : formData?.name || "marksheet.pdf"}</span>
                      {file && <small>{(file.size / 1024 / 1024).toFixed(2)} MB</small>}
                    </span>
                    <button type="button" className="au__x" onClick={handleRemove} aria-label="Remove file" disabled={isDisabled}>✕</button>
                  </div>
                ) : (
                  /* Show upload prompt */
                  <label htmlFor="marksheetInput" className="au__pick">
                    {UP_ICON}
                    <span>
                      <b>Tap to upload</b>
                      <small>JPG, PNG or PDF · max 5 MB</small>
                    </span>
                  </label>
                )}
              </div>

              {/* Error message */}
              {errorMessage && <p className="au__err">{errorMessage}</p>}

              {/* View uploaded file button */}
              {formData?.path && (
                <button
                  type="button"
                  className="au__view-btn"
                  onClick={() => window.open(formData.path, "_blank")}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "6px",
                    background: "none", border: "1px solid #dfe6e4", borderRadius: "10px",
                    padding: "8px 14px", fontWeight: 600, fontSize: "13px",
                    color: "#115e59", cursor: "pointer", marginTop: "8px",
                  }}
                >
                  View Uploaded Marksheet
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      {/* Bottom action bar */}
      <ActionBar
        onBack={() => navigate("/application/academic-information")}
        onNext={handleSubmit}
        backLabel="← Back"
        nextLabel="Continue →"
        nextDisabled={isDisabled}
        nextLoading={saving}
      />

      <NotificationContainer />
      <WhatsAppWidget />
    </div>
  );
};

export default Acedemicaploads;
